/**
 * Hosted scans — "scan a site, a report appears".
 *
 * Enqueue a scan for the Wholisphere runner fleet (no CI / no local runner),
 * and watch the queue status. Rows auto-refresh while any job is in flight so
 * the operator sees queued → claimed → done without reloading.
 */

import { useEffect, useRef, useState } from 'preact/hooks';
import { DashboardApi } from '../api.js';
import type { AuthOrg } from '../auth.js';
import { Banner } from '../components/Banner.js';

const api = new DashboardApi();

interface Job {
  id: string;
  scanId: string;
  status: 'queued' | 'claimed' | 'done' | 'failed';
  attempts: number;
  targets: { urls?: string[]; sitemap?: string };
  createdAt: string;
  updatedAt: string;
}

const STATUS_PILL: Record<Job['status'], string> = {
  queued: 'pill-muted',
  claimed: 'pill-info',
  done: 'pill-supports',
  failed: 'pill-fails',
};

const STATUS_LABEL: Record<Job['status'], string> = {
  queued: 'Queued',
  claimed: 'Scanning…',
  done: 'Done',
  failed: 'Failed',
};

/** Spoken form of each status for the screen-reader announcement. */
const STATUS_ANNOUNCEMENT: Record<Job['status'], string> = {
  queued: 'queued',
  claimed: 'scanning',
  done: 'complete',
  failed: 'failed',
};

const jobTarget = (j: Job): string => j.targets.sitemap ?? j.targets.urls?.[0] ?? 'site';

export function HostedScans({ org }: { org: AuthOrg }): preact.ComponentChildren {
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [productName, setProductName] = useState('');
  const [urlsText, setUrlsText] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  // Screen-reader announcement of job status transitions. Updated only when a
  // job's status actually changes, so the polling itself stays silent.
  const [statusAnnouncement, setStatusAnnouncement] = useState('');
  const prevStatuses = useRef<Map<string, Job['status']>>(new Map());

  useEffect(() => {
    if (!jobs) return;
    const prev = prevStatuses.current;
    const changed: string[] = [];
    for (const j of jobs) {
      const before = prev.get(j.id);
      if (before !== undefined && before !== j.status) {
        changed.push(`Scan of ${jobTarget(j)}: ${STATUS_ANNOUNCEMENT[j.status]}`);
      }
      prev.set(j.id, j.status);
    }
    if (changed.length > 0) setStatusAnnouncement(changed.join('. '));
  }, [jobs]);

  const load = async () => {
    try {
      const r = await api.listHostedScans(org.id);
      setJobs(r.jobs);
      setError(null);
    } catch (e) {
      setError(String(e));
      setJobs([]);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: load is stable
  useEffect(() => {
    void load();
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [org.id]);

  // Poll while any job is in flight; stop when everything is settled.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
  useEffect(() => {
    const inFlight = jobs?.some((j) => j.status === 'queued' || j.status === 'claimed');
    if (inFlight && !timer.current) {
      timer.current = setInterval(() => void load(), 5000);
    } else if (!inFlight && timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }
  }, [jobs]);

  const submit = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      const urls = urlsText
        .split(/[\s,]+/)
        .map((u) => u.trim())
        .filter(Boolean);
      if (urls.length === 0) {
        setError('Enter at least one URL.');
        return;
      }
      const r = await api.enqueueHostedScan(org.id, {
        productName: productName.trim() || (urls[0] ?? ''),
        urls,
      });
      setNotice(`Queued — scan ${r.scanId.slice(0, 8)} will run on the hosted fleet.`);
      setUrlsText('');
      setProductName('');
      await load();
    } catch (e) {
      const detail = e instanceof Error ? e.message : String(e);
      setError(
        `Scan couldn't be queued — try again, and if this keeps happening contact support@wholisphere.ai. (${detail})`,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div class="page-header">
        <h1>Hosted scans</h1>
        <p class="muted">
          Enter a site and our runner fleet scans it — no CI, no local runner. Findings and the VPAT
          land on the scan when it finishes.
        </p>
      </div>

      {error && <Banner variant="warn">{error}</Banner>}

      <div class="card" style={{ marginBottom: 16 }}>
        <h2>Scan a site</h2>
        <form onSubmit={submit}>
          <div class="field">
            <label htmlFor="hs-urls">URLs to scan</label>
            <textarea
              id="hs-urls"
              rows={3}
              placeholder="https://client.com&#10;https://client.com/checkout"
              value={urlsText}
              onInput={(e) => setUrlsText((e.target as HTMLTextAreaElement).value)}
            />
            <p class="muted small" style={{ marginTop: 4 }}>
              One per line (or comma-separated).
            </p>
          </div>
          <div class="field">
            <label htmlFor="hs-name">Product name (optional)</label>
            <input
              id="hs-name"
              type="text"
              placeholder="defaults to the first URL"
              value={productName}
              onInput={(e) => setProductName((e.target as HTMLInputElement).value)}
            />
          </div>
          <div class="form-footer" style={{ marginTop: 12 }}>
            <button type="submit" disabled={busy}>
              {busy ? 'Queuing…' : 'Scan site'}
            </button>
            <output class="small" style={{ marginLeft: 12, color: 'var(--success, #4ade80)' }}>
              {notice}
            </output>
          </div>
        </form>
      </div>

      <div class="card">
        <h2>Recent hosted scans</h2>
        <output class="sr-only">{statusAnnouncement}</output>
        {jobs === null ? (
          <p class="muted">
            <output>Loading…</output>
          </p>
        ) : jobs.length === 0 ? (
          <p class="muted">No hosted scans yet. Queue one above.</p>
        ) : (
          <table class="audit">
            <thead>
              <tr>
                <th>Targets</th>
                <th>Status</th>
                <th>Scan</th>
                <th>Queued</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => {
                const targetLabel = j.targets.sitemap
                  ? j.targets.sitemap
                  : (j.targets.urls?.[0] ?? '—');
                const extra =
                  j.targets.urls && j.targets.urls.length > 1
                    ? ` +${j.targets.urls.length - 1} more`
                    : '';
                return (
                  <tr key={j.id}>
                    <td class="small" style={{ maxWidth: 360, wordBreak: 'break-all' }}>
                      {targetLabel}
                      {extra}
                    </td>
                    <td>
                      <span class={`kind-pill ${STATUS_PILL[j.status]}`}>
                        {STATUS_LABEL[j.status]}
                      </span>
                      {j.attempts > 1 && j.status !== 'done' ? (
                        <span class="small muted"> · retry {j.attempts}</span>
                      ) : null}
                    </td>
                    <td>
                      {j.status === 'done' ? (
                        <code class="small">{j.scanId.slice(0, 8)}</code>
                      ) : (
                        <span class="muted small">—</span>
                      )}
                    </td>
                    <td class="small muted">{new Date(j.createdAt).toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <p class="muted small" style={{ marginTop: 8 }}>
          Completed scans appear on the Scans page, where you can review and publish their VPAT.
        </p>
      </div>
    </div>
  );
}
